import { useEffect, useState, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, MagnifyingGlass, Package, PencilSimple, Trash, WarningCircle, Tag, SlidersHorizontal, LinkSimple, Sparkle, ArrowCircleDown } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useT } from '@/i18n/LanguageContext';
import { useAuth, canEditMfg, hasCap } from '@/auth/AuthContext';
import { mfgApi, ITEM_TYPES, TYPE_BADGE } from './mfgApi';
import { DataTable } from '@/components/ui/DataTable';
import { useDepartment } from './DepartmentContext';
import LabelPrintDialog from '@/components/labels/LabelPrintDialog';
import { buildItemLabel } from '@/components/labels/labelSizes';
import ItemFormDialog from './ItemFormDialog';
import ItemDetailDialog from './ItemDetailDialog';
import IssueStockDialog from './IssueStockDialog';
import DeletedItemsDialog from './DeletedItemsDialog';
import InventoryColumnsDialog from './InventoryColumnsDialog';
import ItemCategoryBulkDialog from './ItemCategoryBulkDialog';
import AiInventoryImportModal from './AiInventoryImportModal';
import { loadConfig, defaultConfig, LS_KEY } from './inventoryColumns';

const Stat = ({ label, value, accent, testid }) => (
  <div data-testid={testid} className="bg-white rounded-xl border border-zinc-200 shadow-sm px-4 py-3">
    <p className="text-xs text-zinc-500">{label}</p>
    <p className={`text-xl font-bold mt-0.5 ${accent || 'text-zinc-900'}`}>{value}</p>
  </div>
);

export default function Inventory() {
  const { t } = useT();
  const { user } = useAuth();
  const canWrite = canEditMfg(user);
  const canDeleteMfg = hasCap(user, 'mfg_delete');
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState('');
  const [type, setType] = useState('all');
  const [lowOnly, setLowOnly] = useState(false);
  const [needsInputOnly, setNeedsInputOnly] = useState(false);
  const [warehouse, setWarehouse] = useState('all');
  const [warehouses, setWarehouses] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [deletedOpen, setDeletedOpen] = useState(false);
  const [labelData, setLabelData] = useState(null);
  const [issueItem, setIssueItem] = useState(null);
  const [colsOpen, setColsOpen] = useState(false);
  const [config, setConfig] = useState(loadConfig);
  const [supplierMap, setSupplierMap] = useState({});
  const [locationMap, setLocationMap] = useState({});
  const [deptMap, setDeptMap] = useState({});
  // Bulk-select state for 分類 assignment. Kept as a Set of item ids so we
  // can quickly add/remove & count. Cleared automatically when items reload
  // (filters change / after bulk save).
  const [selected, setSelected] = useState(() => new Set());
  const [bulkCatOpen, setBulkCatOpen] = useState(false);
  const [aiImportOpen, setAiImportOpen] = useState(false);
  const { active } = useDepartment();
  const deptName = active?.name;
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => { try { localStorage.setItem(LS_KEY, JSON.stringify(config)); } catch { /* ignore */ } }, [config]);

  useEffect(() => {
    mfgApi.suppliers().then((list) => setSupplierMap(Object.fromEntries((list || []).map((s) => [s.id, s.name])))).catch(() => {});
    mfgApi.locations().then((list) => {
      setLocationMap(Object.fromEntries((list || []).map((l) => [l.id, l.code || l.name])));
      setWarehouses((list || []).filter((l) => l.kind === 'warehouse'));
    }).catch(() => {});
    mfgApi.departments().then((list) => setDeptMap(Object.fromEntries((list || []).map((d) => [d.id, d.name])))).catch(() => {});
  }, []);

  const toggleCol = (key) => setConfig((cfg) => ({ ...cfg, columns: cfg.columns.map((c) => (c.key === key ? { ...c, visible: !c.visible } : c)) }));
  const moveCol = (key, delta) => setConfig((cfg) => {
    const arr = [...cfg.columns];
    const i = arr.findIndex((c) => c.key === key);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= arr.length) return cfg;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return { ...cfg, columns: arr };
  });
  const setSortCfg = (key, dir) => setConfig((cfg) => ({ ...cfg, sort: { key, dir: dir || 'asc' } }));
  const resetCols = () => setConfig(defaultConfig());

  // Quick-start deep link: /mfg/inventory?new=1 auto-opens the new-item dialog.
  useEffect(() => {
    if (searchParams.get('new') === '1' && canWrite) {
      setEditItem(null);
      setFormOpen(true);
      searchParams.delete('new');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, canWrite, setSearchParams]);

  const load = useCallback(() => {
    const params = {};
    if (type !== 'all') params.type = type;
    if (q.trim()) params.q = q.trim();
    if (lowOnly) params.low_stock = true;
    if (needsInputOnly) params.needs_input = true;
    if (warehouse !== 'all') params.warehouse_id = warehouse;
    mfgApi.items(params).then(setItems).catch(() => {});
    mfgApi.itemStats().then(setStats).catch(() => {});
    // Filter change → drop selection so buyer isn't left with hidden picks
    setSelected(new Set());
  }, [type, q, lowOnly, needsInputOnly, warehouse]);
  useEffect(() => { load(); }, [load]);

  const toggleSelect = (id, checked) => setSelected((prev) => {
    const next = new Set(prev);
    if (checked) next.add(id); else next.delete(id);
    return next;
  });
  const allSelected = items.length > 0 && items.every((it) => selected.has(it.id));
  const someSelected = items.some((it) => selected.has(it.id));
  const toggleSelectAll = () => setSelected((prev) => {
    if (allSelected) return new Set();
    const next = new Set(prev);
    items.forEach((it) => next.add(it.id));
    return next;
  });

  const del = async (it) => {
    if (!window.confirm(t('mfg.confirmDelete', { name: it.name }))) return;
    try { await mfgApi.deleteItem(it.id); toast.success(t('mfg.deleted')); load(); }
    catch (e) {
      if (e?.response?.status === 409) {
        if (!window.confirm(t('mfg.confirmForceDelete', { name: it.name }))) return;
        try { await mfgApi.deleteItem(it.id, true); toast.success(t('mfg.forceDeleted')); load(); }
        catch (e2) { toast.error(e2?.response?.data?.detail || t('mfg.error')); }
        return;
      }
      toast.error(e?.response?.data?.detail || t('mfg.error'));
    }
  };

  const fmtMoney = (n) => '¥' + (Number(n) || 0).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' });

  // 自由文の属性列。2行で打ち切り全文は title に入れる（行が膨らまないように）。
  // `block` は付けない: line-clamp より display が後勝ちしてクランプが無効になる。
  const TextCell = ({ v }) => <span className="line-clamp-2 break-words text-zinc-600 text-xs" title={typeof v === 'string' ? v : ''}>{v}</span>;

  const makeColumn = (key) => {
    switch (key) {
      case 'sku': return { key: 'sku', header: t('mfg.item.sku'), width: 'w-28', mono: true, sortable: true, sortValue: (it) => it.sku, cell: (it) => <span className="block truncate font-mono text-zinc-600" title={it.sku || ''}>{it.sku}</span> };
      case 'name': return { key: 'name', primary: true, flex: true, header: t('mfg.item.name'), sortable: true, sortValue: (it) => it.name, cell: (it) => <span className="block min-w-0 font-medium text-zinc-800"><span className="block truncate" title={it.name} data-no-translate>{it.name}</span><span className="block truncate text-[11px] font-normal text-zinc-400" title={it.category || ''}>{it.category || ''}</span></span> };
      case 'type': return { key: 'type', header: t('mfg.item.type'), width: 'w-28', sortable: true, sortValue: (it) => t(`mfg.type.${it.type}`), cell: (it) => <Badge variant="outline" className={TYPE_BADGE[it.type]}>{t(`mfg.type.${it.type}`)}</Badge> };
      case 'model': return { key: 'model', header: t('mfg.item.model'), width: 'w-24', sortable: true, sortValue: (it) => it.model_no || '', cell: (it) => <TextCell v={it.model_no || '—'} /> };
      case 'maker_pn': return { key: 'maker_pn', header: t('mfg.item.makerPn'), width: 'w-32', mono: true, sortable: true, sortValue: (it) => it.maker_pn || '', cell: (it) => <span className="font-mono text-zinc-500 text-xs">{it.maker_pn || '—'}</span> };
      case 'url': return { key: 'url', header: 'URL', width: 'w-12', sortable: false, cell: (it) => (it.mfg_url ? <a href={it.mfg_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} data-testid={`item-url-link-${it.id}`} title={it.mfg_url} className="inline-flex items-center text-orange-600 hover:text-orange-700"><LinkSimple size={15} weight="bold" /></a> : <span className="text-zinc-300 text-xs">—</span>) };
      case 'on_hand': return { key: 'on_hand', header: t('mfg.item.onHand'), width: 'w-24', align: 'right', sortable: true, sortValue: (it) => Number(it.on_hand) || 0, cell: (it) => <span className="whitespace-nowrap font-semibold tabular-nums">{it.on_hand} <span className="text-xs font-normal text-zinc-400">{it.uom}</span></span> };
      case 'status': return null;   // 2026-11-05 · 在庫アラート/要入力は「操作」列にまとめた
      case 'category': return { key: 'category', header: t('mfg.item.category'), width: 'w-32', sortable: true, sortValue: (it) => it.category || '', cell: (it) => <TextCell v={it.category || '—'} /> };
      case 'maker': return { key: 'maker', header: t('mfg.item.maker'), width: 'w-32', sortable: true, sortValue: (it) => it.maker || '', cell: (it) => <TextCell v={it.maker || '—'} /> };
      case 'departments': return { key: 'departments', header: t('mfg.item.departments'), width: 'w-36', sortable: true, sortValue: (it) => (it.departments || []).map((d) => deptMap[d] || '').join(','), cell: (it) => <TextCell v={(it.departments || []).map((d) => deptMap[d]).filter(Boolean).join('、') || '—'} /> };
      case 'uom': return { key: 'uom', header: t('mfg.item.uom'), width: 'w-16', sortable: true, sortValue: (it) => it.uom || '', cell: (it) => <span className="text-zinc-600 text-xs">{it.uom || '—'}</span> };
      case 'reorder_point': return { key: 'reorder_point', header: t('mfg.item.reorderPoint'), width: 'w-24', align: 'right', sortable: true, sortValue: (it) => Number(it.reorder_point) || 0, cell: (it) => <span className="tabular-nums text-zinc-600">{it.reorder_point ?? '—'}</span> };
      case 'max_stock': return { key: 'max_stock', header: t('mfg.item.maxStock'), width: 'w-24', align: 'right', sortable: true, sortValue: (it) => (it.max_stock == null ? -1 : Number(it.max_stock)), cell: (it) => <span className="tabular-nums text-zinc-600">{it.max_stock ?? '—'}</span> };
      case 'cost': return { key: 'cost', header: t('mfg.item.cost'), width: 'w-28', align: 'right', sortable: true, sortValue: (it) => Number(it.cost) || 0, cell: (it) => <span className="tabular-nums text-zinc-600">{fmtMoney(it.cost)}</span> };
      case 'value': return { key: 'value', header: t('mfg.stats.value'), width: 'w-32', align: 'right', sortable: true, sortValue: (it) => (Number(it.on_hand) || 0) * (Number(it.cost) || 0), cell: (it) => <span className="tabular-nums font-medium text-orange-600">{fmtMoney((Number(it.on_hand) || 0) * (Number(it.cost) || 0))}</span> };
      case 'supplier': return { key: 'supplier', header: t('mfg.item.supplier'), width: 'w-36', sortable: true, sortValue: (it) => supplierMap[it.supplier_id] || '', cell: (it) => <TextCell v={supplierMap[it.supplier_id] || '—'} /> };
      case 'location': return { key: 'location', header: t('mfg.item.defaultLocation'), width: 'w-32', sortable: true, sortValue: (it) => locationMap[it.default_location_id] || '', cell: (it) => <TextCell v={locationMap[it.default_location_id] || '—'} /> };
      case 'barcode': return { key: 'barcode', header: t('mfg.item.barcode'), width: 'w-32', mono: true, sortable: true, sortValue: (it) => it.barcode || '', cell: (it) => <span className="font-mono text-xs text-zinc-500">{it.barcode || '—'}</span> };
      case 'lot_tracked': return { key: 'lot_tracked', header: t('mfg.item.lotTracked'), width: 'w-20', sortable: true, sortValue: (it) => (it.lot_tracked ? 1 : 0), cell: (it) => <span className="text-zinc-600 text-xs">{it.lot_tracked ? t('mfg.yes') : t('mfg.no')}</span> };
      default: return null;
    }
  };

  // 2026-11-05 · バッジ（在庫少 / 過剰 / 要入力）と操作を1列にまとめ、見出しを付ける。
  // 別々の見出し無し列だと、品目名が潰れるのに余白だけが残っていた。
  const statusBadges = (it) => (<>
    {it.low_stock && <Badge variant="outline" className="shrink-0 whitespace-nowrap bg-rose-50 text-rose-700 border-rose-200">{t('mfg.badge.low')}</Badge>}
    {it.overstock && <Badge variant="outline" className="shrink-0 whitespace-nowrap bg-amber-50 text-amber-700 border-amber-200">{t('mfg.badge.over')}</Badge>}
    {it.needs_input && <Badge variant="outline" data-testid={`item-needs-input-${it.id}`} title={`${t('mfg.item.missingFields')}: ${(it.missing_required || []).join('、')}`} className="shrink-0 whitespace-nowrap bg-amber-50 text-amber-800 border-amber-300">{t('mfg.item.needsInput')}</Badge>}
  </>);

  const actionsColumn = { key: 'actions', actions: true, header: t('mfg.cols.actions'), width: 'w-52', cell: (it) => (<>
    {statusBadges(it)}
    {canWrite && <Button size="sm" variant="ghost" data-testid={`item-issue-${it.id}`} onClick={() => setIssueItem(it)} className="relative z-10 h-9 px-1.5 text-rose-700 hover:bg-rose-50" title={t('mfg.action.issue')}><ArrowCircleDown size={16} /></Button>}
    <Button size="sm" variant="ghost" data-testid={`item-label-${it.id}`} onClick={() => setLabelData([buildItemLabel(it, deptName)])} className="h-9 px-1.5 text-zinc-600" title={t('mfg.label.printRow')}><Tag size={16} /></Button>
    {canWrite && <Button size="sm" variant="ghost" data-testid={`item-edit-${it.id}`} onClick={() => { setEditItem(it); setFormOpen(true); }} className="h-9 px-1.5 text-zinc-600"><PencilSimple size={16} /></Button>}
    {canDeleteMfg && <Button size="sm" variant="ghost" data-testid={`item-delete-${it.id}`} onClick={() => del(it)} className="h-9 px-1.5 text-red-600 hover:bg-red-50"><Trash size={16} /></Button>}
  </>) };

  // Checkbox column — first column when user has write permission. Uses
  // `mobileBadge` so the checkbox appears in the mobile card's title row
  // (not hidden). Clicks stop propagation so ticking a row doesn't ALSO
  // open the item detail dialog.
  const selectColumn = canWrite ? {
    key: 'select',
    header: (
      <input
        type="checkbox"
        data-testid="items-select-all"
        checked={allSelected}
        ref={(el) => { if (el) el.indeterminate = !allSelected && someSelected; }}
        onChange={toggleSelectAll}
        onClick={(e) => e.stopPropagation()}
        className="h-4 w-4 rounded border-zinc-300 accent-orange-500 cursor-pointer align-middle"
        title="全て選択 / 解除"
      />
    ),
    width: 'w-10',
    sortable: false,
    mobileBadge: true,
    cell: (it) => (
      <input
        type="checkbox"
        data-testid={`item-select-${it.id}`}
        checked={selected.has(it.id)}
        onChange={(e) => toggleSelect(it.id, e.target.checked)}
        onClick={(e) => e.stopPropagation()}
        className="h-4 w-4 rounded border-zinc-300 accent-orange-500 cursor-pointer"
      />
    ),
  } : null;

  const columns = [selectColumn, ...config.columns.filter((c) => c.visible).map((c) => makeColumn(c.key)).filter(Boolean), actionsColumn].filter(Boolean);

  return (
    <div className="space-y-5" data-testid="mfg-inventory">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-2xl font-bold tracking-tight text-zinc-900">{t('mfg.nav.inventory')}</h1>
        {canWrite && (
          <div className="flex gap-2 flex-wrap">
            <Button data-testid="bulk-label-btn" variant="outline" onClick={() => items.length && setLabelData(items.map((it) => buildItemLabel(it, deptName)))} disabled={!items.length} className="h-11 rounded-lg">
              <Tag size={16} className="mr-1.5" /> {t('mfg.label.bulkPrint')}
            </Button>
            <Button data-testid="deleted-items-btn" variant="outline" onClick={() => setDeletedOpen(true)} className="h-11 rounded-lg">
              <Trash size={16} className="mr-1.5" /> {t('mfg.deletedItems.btn')}
            </Button>
            <Button data-testid="ai-import-btn" variant="outline" onClick={() => setAiImportOpen(true)} className="h-11 rounded-lg border-violet-200 text-violet-700 hover:bg-violet-50">
              <Sparkle size={16} weight="fill" className="mr-1.5" /> AIインポート
            </Button>
            <Button data-testid="ai-import-history-btn" variant="outline" asChild className="h-11 rounded-lg border-violet-200 text-violet-700 hover:bg-violet-50">
              <Link to="/settings/ai-import-history" title="AIインポート履歴・一括取消">履歴</Link>
            </Button>
            <Button data-testid="item-new-btn" onClick={() => { setEditItem(null); setFormOpen(true); }} className="h-11 rounded-lg bg-teal-700 hover:bg-teal-800 shadow-sm">
              <Plus size={18} className="mr-1.5" weight="bold" /> {t('mfg.item.new')}
            </Button>
          </div>
        )}
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="inventory-stats">
          <Stat label={t('mfg.stats.items')} value={stats.total_items} testid="stat-items" />
          <Stat label={t('mfg.stats.lowStock')} value={stats.low_stock} accent={stats.low_stock ? 'text-rose-600' : 'text-zinc-900'} testid="stat-low" />
          <Stat label={t('mfg.stats.overstock')} value={stats.overstock} accent={stats.overstock ? 'text-amber-600' : 'text-zinc-900'} testid="stat-over" />
          <Stat label={t('mfg.stats.expiring')} value={stats.expiring_lots} accent={stats.expiring_lots ? 'text-amber-600' : 'text-zinc-900'} testid="stat-expiring" />
          <Stat label={t('mfg.stats.needsInput')} value={stats.needs_input ?? 0} accent={stats.needs_input ? 'text-amber-700' : 'text-zinc-900'} testid="stat-needs-input" />
          <Stat label={t('mfg.stats.value')} value={fmtMoney(stats.total_value)} accent="text-orange-600" testid="stat-value" />
        </div>
      )}

      {/* A4 · 「要入力」バナー。保存はブロックしないので、ここから探して直す */}
      {stats?.needs_input > 0 && !needsInputOnly && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-center gap-3 flex-wrap" data-testid="items-needs-input-banner">
          <WarningCircle size={18} weight="fill" className="text-amber-600" />
          <p className="text-sm text-amber-900">
            {t('mfg.inventory.needsInputBanner', { count: stats.needs_input })}
            <span className="ml-2 text-xs text-amber-700">
              {Object.entries(stats.missing_by_field || {}).filter(([, n]) => n > 0).map(([k, n]) => `${k} ${n}`).join(' / ')}
            </span>
          </p>
          <div className="flex-1" />
          <Button size="sm" variant="outline" data-testid="items-needs-input-banner-btn"
            onClick={() => setNeedsInputOnly(true)} className="h-9 border-amber-300 text-amber-900 hover:bg-amber-100">
            {t('mfg.inventory.needsInputOnly')}
          </Button>
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input data-testid="item-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('mfg.inventory.searchPh')} className="h-10 pl-9 rounded-lg" />
        </div>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger data-testid="item-type-filter" className="h-10 w-40 rounded-lg"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">{t('mfg.item.type')}: {t('mfg.all')}</SelectItem>{ITEM_TYPES.map((tp) => <SelectItem key={tp} value={tp}>{t(`mfg.type.${tp}`)}</SelectItem>)}</SelectContent>
        </Select>
        {warehouses.length > 0 && (
          <Select value={warehouse} onValueChange={setWarehouse}>
            <SelectTrigger data-testid="item-warehouse-filter" className="h-10 w-44 rounded-lg"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('mfg.location.warehouse')}: {t('mfg.all')}</SelectItem>
              {warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name || w.code}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Button variant={lowOnly ? 'default' : 'outline'} onClick={() => setLowOnly((v) => !v)} data-testid="item-low-toggle" className={`h-10 rounded-lg ${lowOnly ? 'bg-rose-500 hover:bg-rose-600' : ''}`}>
          <WarningCircle size={16} className="mr-1.5" /> {t('mfg.inventory.lowOnly')}
        </Button>
        <Button variant={needsInputOnly ? 'default' : 'outline'} onClick={() => setNeedsInputOnly((v) => !v)} data-testid="item-needs-input-toggle" className={`h-10 rounded-lg ${needsInputOnly ? 'bg-amber-600 hover:bg-amber-700' : ''}`}>
          <WarningCircle size={16} className="mr-1.5" /> {t('mfg.inventory.needsInputOnly')}
        </Button>
        <Button variant="outline" onClick={() => setColsOpen(true)} data-testid="columns-btn" className="h-10 rounded-lg">
          <SlidersHorizontal size={16} className="mr-1.5" /> {t('mfg.cols.title')}
        </Button>
      </div>

      {/* Bulk-select action bar — appears when 1+ items are ticked. Mirrors
          /shizai/requests' bulk bar so the UX is identical. */}
      {canWrite && selected.size > 0 && (
        <div className="bg-orange-50 border-2 border-orange-200 rounded-xl px-4 py-3 flex items-center gap-3 flex-wrap shadow-sm sticky top-2 z-10" data-testid="items-bulk-bar">
          <span className="text-sm font-semibold text-orange-900">選択中 {selected.size}件</span>
          <span className="text-xs text-orange-700">複数の品目にまとめて分類を設定</span>
          <div className="flex-1" />
          <Button size="sm" variant="outline" onClick={() => setSelected(new Set())}
            data-testid="items-bulk-clear" className="h-9">
            選択解除
          </Button>
          <Button size="sm" onClick={() => setBulkCatOpen(true)}
            data-testid="items-bulk-cat-btn"
            className="h-9 bg-teal-700 hover:bg-teal-800 shadow-sm">
            分類を一括設定 ({selected.size})
          </Button>
        </div>
      )}

      <DataTable
        key={`cols-${config.sort.key || 'none'}-${config.sort.dir}`}
        dataTestId="item-table"
        rows={items}
        rowKey={(it) => it.id}
        rowTestId={(it) => `item-row-${it.id}`}
        onRowClick={(it) => setDetailId(it.id)}
        defaultSort={config.sort}
        empty={{ icon: <Package size={40} weight="duotone" className="text-zinc-300 mx-auto mb-2" />, text: t('mfg.inventory.empty'), testid: 'item-empty' }}
        columns={columns}
        minWidth="min-w-[960px]"
      />

      <InventoryColumnsDialog open={colsOpen} onClose={() => setColsOpen(false)} config={config}
        onToggle={toggleCol} onMove={moveCol} onSort={setSortCfg} onReset={resetCols} />

      <ItemFormDialog open={formOpen} onClose={() => setFormOpen(false)} item={editItem} onSaved={load} defaultDepartmentId={active?.id} />
      <ItemDetailDialog open={!!detailId} onClose={() => setDetailId(null)} itemId={detailId} onChanged={load} />
      <IssueStockDialog open={!!issueItem} onClose={() => setIssueItem(null)} item={issueItem} onDone={load} />
      <DeletedItemsDialog open={deletedOpen} onClose={() => setDeletedOpen(false)} onChanged={load} />
      <LabelPrintDialog open={!!labelData} onClose={() => setLabelData(null)} labels={labelData || []} title="品目ラベル" />
      <AiInventoryImportModal
        open={aiImportOpen}
        onClose={() => setAiImportOpen(false)}
        defaultDepartmentId={active?.id}
        onImported={load}
      />
      {bulkCatOpen && (() => {
        const ids = Array.from(selected);
        const chosen = items.filter((it) => selected.has(it.id));
        const hint = chosen.slice(0, 3).map((it) => it.name || it.sku).join('、')
          + (chosen.length > 3 ? ` 他${chosen.length - 3}件` : '');
        return (
          <ItemCategoryBulkDialog
            open
            itemIds={ids}
            hint={hint}
            onClose={() => setBulkCatOpen(false)}
            onSaved={() => { setBulkCatOpen(false); setSelected(new Set()); load(); }}
          />
        );
      })()}
    </div>
  );
}
